import { Navigate, NavLink, Outlet, useOutletContext } from 'react-router';
import { useMe } from '../api/hooks';
import { logout } from '../auth/keycloak';
import { ErrorNote, Loading, RoleBadge } from './ui';

type Permission = NonNullable<ReturnType<typeof useMe>['data']>['permissions'][number];

/** Pages de l'interface, dans l'ordre du menu, avec la permission de lecture qui les autorise. */
const PAGES: Array<{ to: string; label: string; permission: Permission }> = [
  { to: '/requests', label: 'Demandes', permission: 'requests:read' },
  { to: '/agents', label: 'Agents', permission: 'agents:read' },
  { to: '/decrees', label: 'Arrêtés', permission: 'decrees:read' },
  { to: '/positions', label: 'Postes', permission: 'positions:read' },
  { to: '/services', label: 'Organigramme', permission: 'services:read' },
  { to: '/workflows', label: 'Circuits', permission: 'workflows:read' },
  { to: '/roles', label: 'Comptes et rôles', permission: 'roles:read' },
  { to: '/audit', label: 'Journal', permission: 'audit:read' },
];

/** Première page autorisée après connexion : un agent arrive sur ses demandes, un administrateur sur les comptes. */
export function Start() {
  const me = useOutletContext<{ permissions: Permission[] }>();
  const first = PAGES.find((p) => me.permissions.includes(p.permission));
  return <Navigate to={first?.to ?? '/services'} replace />;
}

export function Shell() {
  const me = useMe();
  if (me.isPending) return <Loading what="Ouverture de la session" />;
  if (me.error) {
    return (
      <div className="landing">
        <div className="card">
          <h1>Accès impossible</h1>
          <ErrorNote error={me.error} />
          <button className="btn" onClick={() => void logout()}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }
  const { user, tenant, roles, perimeter, permissions } = me.data;
  const roleSet = new Set(roles.map((r) => r.role));
  // Le menu suit la matrice de permissions de l'API, jamais une liste de rôles recopiée côté interface.
  const pages = PAGES.filter((p) => permissions.includes(p.permission));

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          Manu.ia
          <small>{tenant.name}</small>
        </div>
        <nav className="nav" aria-label="Navigation">
          {pages.map((p) => (
            <NavLink key={p.to} to={p.to}>
              {p.label}
            </NavLink>
          ))}
        </nav>
        <div className="who">
          <div className="name">{user.displayName}</div>
          <div className="muted small">{user.username}</div>
          <div style={{ margin: '6px 0' }}>{[...roleSet].map((r) => <RoleBadge key={r} role={r} />)}</div>
          <div className="muted small">
            Périmètre :{' '}
            {perimeter.kind === 'tenant' ? 'toute la collectivité' : perimeter.kind === 'services' ? `${perimeter.serviceIds.length} service(s)` : perimeter.kind === 'self' ? 'mon dossier' : 'aucun dossier'}
          </div>
          <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => void logout()}>
            Se déconnecter
          </button>
        </div>
      </aside>
      <main className="main">
        <Outlet context={me.data} />
      </main>
    </div>
  );
}
